import { inject, Injectable } from '@angular/core';
import { ODataServiceFactory } from 'angular-odata';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Gl } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { GenericOrganisationDataService } from './generic-organisation-data.service';

@Injectable({
  providedIn: 'root',
})
export class GlDataService extends GenericOrganisationDataService<Gl> {
  private factory = inject(ODataServiceFactory);

  constructor() {
    super();
    this.dataServices = this.factory.entitySet<Gl>(
      'Gls',
      'QualicoatBackend.Domain.Crm.OrganisationTypes.Gl'
    );
    this.expandedString = 'Contacts,Contacts($orderby=FirstName),Addresses,AssignedLaboratories,AssignedCustomers';
  }

  fetch(organisationId: string): Observable<Gl> {
    return this.dataServices.entities().query((q) => {
      q.expand(this.expandedString);
    })
      .entity(organisationId)
      .fetch()
      .pipe(map((oDataEntity) => oDataEntity.entity!));
  }

  fetchGlNamesForFilter(): Observable<string[]> {
    return this.dataServices.entities()
      .query((q) => {
        q.select(['name']);
        q.orderBy('name asc');
      })
      .fetch()
      .pipe(map(({ entities }) => (entities || []).map(gl => gl.name)));
  }

  fetchGlCustomerMapping(): Observable<Map<string, string>> {
    return this.dataServices.entities()
      .query((q) => {
        q.select(['name']);
        q.expand('AssignedCustomers($select=id)');
      })
      .fetch()
      .pipe(map(({ entities }) => {
        const mapping = new Map<string, string>();
        (entities || []).forEach(gl => {
          gl.assignedCustomers?.forEach((customer: { id: string }) => {
            mapping.set(customer.id, gl.name);
          });
        });
        return mapping;
      }));
  }

  createGl(gl: Partial<Gl>): Observable<string> {
    const glForCreate: { [key: string]: unknown } = {};
    Object.entries(gl).forEach(([key, val]) => {
      if (key === 'assignedCountries') {
        glForCreate[key] = val ?? [];
      } else {
        if (val !== '') {
          glForCreate[key] = val;
        }
      }
    });
    return this.httpClient.post<string>(`/api/gls`, gl);
  }

  updateGl(glId: string, patch: Partial<Gl>): Observable<Gl> {
    return this.httpClient.put<Gl>(`/api/gls/${glId}`, patch);
  }

  fetchCustomerStats(customerIds: string[]): Observable<CustomerStats[]> {
    return this.httpClient.post<CustomerStats[]>('/api/v1/customers/stats', customerIds);
  }
}

export interface CustomerStats {
  customerId: string;
  licenseCount: number;
  seasideCount: number;
  preOxCount: number;
  postformingCount: number;
  coatingSystemCount: number;
  pretreatmentSystemCount: number;
}
